import React from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';

const byId = (left, right) => String(left ?? '') === String(right ?? '');

const RelationalAutocomplete = ({
  ariaLabel,
  options = [],
  valueId,
  legacyLabel = '',
  onChange,
  loading = false,
  disabled = false,
  placeholder = 'Select an option',
  emptyText = 'No options available',
  getOptionLabel = (option) => option?.name || '',
}) => {
  const selectedOption = options.find((option) => byId(option.id, valueId));
  const legacyOption = !selectedOption && legacyLabel
    ? { id: `legacy:${legacyLabel}`, name: legacyLabel, isLegacy: true }
    : null;
  const value = selectedOption || legacyOption;
  const displayedOptions = legacyOption ? [legacyOption, ...options] : options;

  return (
    <Autocomplete
      fullWidth
      size="small"
      openOnFocus
      options={displayedOptions}
      value={value || null}
      loading={loading}
      disabled={disabled || (options.length === 0 && !legacyOption)}
      noOptionsText={emptyText}
      getOptionLabel={(option) => (
        option?.isLegacy ? option.name : getOptionLabel(option)
      )}
      getOptionDisabled={(option) => Boolean(option?.isLegacy)}
      isOptionEqualToValue={(option, selected) => byId(option?.id, selected?.id)}
      onChange={(_event, option) => {
        if (option?.isLegacy) return;
        onChange(option || null);
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.isLegacy ? `Current legacy value: ${option.name}` : getOptionLabel(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value ? '' : placeholder}
          helperText={legacyOption ? 'Current value is not linked to active Master Data.' : ''}
          inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        '& .MuiOutlinedInput-root': {
          minHeight: 40,
          borderRadius: '10px',
          backgroundColor: '#fff',
          paddingTop: '1px',
          paddingBottom: '1px',
          '& fieldset': { borderColor: '#e5e7eb' },
          '&:hover fieldset': { borderColor: '#3DAC5A' },
          '&.Mui-focused fieldset': { borderColor: '#3DAC5A' },
        },
        '& .MuiFormHelperText-root': {
          marginLeft: '4px',
        },
      }}
    />
  );
};

export default RelationalAutocomplete;
